import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import NameRecurrenceSummaryTable from "./NameRecurrenceSummaryTable";

const NameRecurrencesReview: React.FC<any> = () => {
  const [nameRecurrenceCountries, setNameRecurrenceCountries] = React.useState<
    any[]
  >([]);
  const [selectedCountryCode, setSelectedCountryCode] = React.useState<
    string | null
  >(null);
  const [loadingCountries, setLoadingCountries] = React.useState<boolean>(true);

  const { showError } = useError();
  const { apiRequest } = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch countries for name recurrences review
  useEffect(() => {
    const fetchNameRecurrCountries = async () => {
      try {
        const response = await apiRequest<
          {
            country_code: string | null | undefined;
            country_name: string | null | undefined;
          }[]
        >(`/brand_utils/name_recurrence_countries`, {
          method: "GET",
        });

        if (response.data) {
          const validCountries = response.data.filter(
            (item): item is { country_code: string; country_name: string } =>
              typeof item.country_code === "string" &&
              item.country_code.trim() !== ""
          );

          const sortedCountries = [...validCountries].sort((a, b) =>
            a.country_code.localeCompare(b.country_code)
          );
          setNameRecurrenceCountries(sortedCountries);

          const params = new URLSearchParams(location.search);
          const countryCode = params.get("country");
          if (
            countryCode &&
            sortedCountries.some((c) => c.country_code === countryCode)
          ) {
            setSelectedCountryCode(countryCode);
          }
        }
      } catch (error) {
        showError(`Failed to fetch countries for name recurrences review.`);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchNameRecurrCountries();
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const countryCode = params.get("country");

    // Set selected country from URL if valid
    if (countryCode && nameRecurrenceCountries.length > 0) {
      const matchingCountry = nameRecurrenceCountries.find(
        (c) => c.country_code.toLowerCase() === countryCode.toLowerCase()
      );

      if (matchingCountry) {
        setSelectedCountryCode(matchingCountry.country_code);
      }
    }
  }, [location.search, nameRecurrenceCountries]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "auto",
        height: "auto",
        minHeight: "500px",
        overflow: "hidden",
        border: "1px solid #ccc",
        borderRadius: "4px",
        p: 5,
        mt: 2,
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="h6" sx={{ mb: 5 }}>
        Name Recurrences Review
      </Typography>
      <Typography variant="body1">
        {selectedCountryCode
          ? ""
          : "Select a country to begin chain_id tagging review for name recurrences."}
      </Typography>

      <Box sx={{ mt: 2, mb: 5, width: 300 }}>
        {loadingCountries ? (
          <CircularProgress />
        ) : (
          <Autocomplete
            options={nameRecurrenceCountries}
            getOptionLabel={(option) =>
              `${option.country_code.toUpperCase()}: ${option.country_name}`
            }
            filterOptions={(options, { inputValue }) =>
              options.filter(
                (option) =>
                  option.country_code
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.country_name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Country"
                variant="outlined"
              />
            )}
            value={
              nameRecurrenceCountries.length > 0
                ? nameRecurrenceCountries.find(
                    (country) => country.country_code === selectedCountryCode
                  ) || null
                : null
            }
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedCountryCode(newValue.country_code);
                navigate(
                  { search: `?country=${newValue.country_code}` },
                  { replace: true }
                );
              } else {
                setSelectedCountryCode(null);
                navigate({ search: "" }, { replace: true });
              }
            }}
            isOptionEqualToValue={(option, value) =>
              option.country_code === value.country_code
            }
          />
        )}
      </Box>

      <Box>
        {selectedCountryCode && (
          <NameRecurrenceSummaryTable
            country_code={selectedCountryCode}
            name_recurrence_type="partially_filled_chains"
          />
        )}
      </Box>
    </Box>
  );
};

export default NameRecurrencesReview;
